import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { marked } from 'marked';
import { useAppShell } from '../contexts/AppShellContext';
import { useAuth } from '../contexts/AuthContext';
import { useChats, type ChatSessionLocal } from '../contexts/ChatsContext';
import { CameraModal } from '../components/modals/CameraModal';
import { MatchResultsCard } from '../components/chat/MatchResultsCard';
import { ChatActions } from '../components/chat/ChatActions';
import { statusPillHtml } from '../components/StatusPill';
import { ReceiptDrawer } from '../components/chat/ReceiptDrawer';
import { MaterialAllocationPanel } from '../components/chat/MaterialAllocationPanel';
import { PdfViewerPanel } from '../components/chat/PdfViewerPanel';
import { chatWithAgent } from '../services/agentService';
import { uploadPhoto, getMatchStatus, getMatchResults } from '../services/jobService';
import { pushReceiptToJobsDb } from '../services/jobsDbService';
import { createMicMailBucket, formatMicBucketNote } from '../services/micService';
import { getMockMatchResult } from '../data/mockMatchResults';
import { draftFromRow, buildReceiptPatch, draftToMatchJobs, orderedFor, shortageSummary, oversSummary, buildProxyReceivedLines, noteStamp, type ReceiptDraft } from '../data/receiptDraft';
import { createMaterialReceipt, updateMaterialReceipt, confirmReceiptMatch, getMaterialReceipts, mergeReorderReceipt, getReceiptThread, postReceiptThreadMessage, toThreadPayload } from '../services/materialReceiptService';
import { todayIso } from '../utils/timeWindow';
import { ESCALATION_TARGETS, teamLabel, PAGE_PATHS, pageVisible } from '../utils/rbac';
import onxAgentLogo from '../assets/onx-agent-logo.png';
import { CAWorkflowPanel } from '../components/ca/CAWorkflowPanel';
import { AdvisorRegistrationPanel } from '../components/advisor/AdvisorRegistrationPanel';
import { useFloorState } from '../hooks/useFloorState';
import { FloorStateCard } from '../components/FloorStateCard';
import type { FloorCard } from '../services/floorStateService';
import { trackEvent } from '../services/telemetryService';
import { useProxyData } from '../contexts/ProxyDataContext';
import { useEmailInbox } from '../contexts/EmailInboxContext';
import { useTeamMessages } from '../contexts/TeamMessagesContext';
import { matchProxyIntent } from '../utils/proxyAceIntents';
import { runProxyIntent } from '../utils/proxyAceHandlers';
import { ProxyTaskPanel } from '../components/proxy/ProxyTaskPanel';
import { buildRdqOrderTask, buildProxyIntakeSummary, buildProxyIntakeTasks, shortageResolved, PROXY_WORKFLOW_REGISTRY, type ProxyTask } from '../data/proxyWorkflowRegistry';
import { useProxyWorkflowContext } from '../hooks/useProxyWorkflowContext';
import { hasOnlySkippedMaterial, resolveProxyJob } from '../utils/proxyQueue';


declare global { interface Window { currentMatchResult?: any; } }

marked.setOptions({ breaks: true, gfm: true });
function mdToHtml(text) {
  if (!text || /<[a-z][\s\S]*>/i.test(text)) return text;
  return marked.parse(text);
}

const TODAY = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

// Relative-then-absolute timestamp ladder (Slack/Linear convention).
// `ts` is the ISO string stamped on every message by ChatsContext.appendMsg.
function relTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const ms = d.getTime();
  if (Number.isNaN(ms)) return '';
  const diff = (Date.now() - ms) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  const hhmm = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `Yesterday ${hhmm}`;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${hhmm}`;
}
// Full UTC audit string for the hover tooltip
function utcStr(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().replace('T', ' ').replace(/\.\d+Z$/, ' UTC');
}

function stripHtml(html) {
  return (html || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

// Collapse runs of consecutive agent "trace" messages (intermediate tool /
// reasoning steps) into a single unit; everything else stays a plain message.
function buildUnits(msgs) {
  const units = [];
  for (const m of msgs) {
    if (m.role === 'agent' && m.trace) {
      const last = units[units.length - 1];
      if (last && last.type === 'trace') last.items.push(m);
      else units.push({ type: 'trace', key: m.id, items: [m] });
    } else {
      units.push({ type: 'msg', key: m.id, msg: m });
    }
  }
  return units;
}

const CopyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

// Ace logo wrapped in the idle / thinking / hero glow ring (state via CSS class)
function AgentAvatar({ size = 32, thinking = false, hero = false }) {
  const inner = Math.round(size * 0.72);
  return (
    <span
      className={`agent-icon-wrap${thinking ? ' is-thinking' : ''}${hero ? ' agent-icon-large' : ''}`}
      style={{ width: size, height: size }}
    >
      <img src={onxAgentLogo} alt="Ace" className="object-contain" style={{ width: inner, height: inner }} />
    </span>
  );
}

function TypingIndicator() {
  return (
    <div className="flex items-center gap-3">
      <AgentAvatar thinking />
      <div className="thinking-dots"><span /><span /><span /></div>
    </div>
  );
}

// Indented, compact, collapsible cluster of intermediate agent steps.
// Auto-expanded while it's the latest unit (live), auto-collapses once a
// later message arrives, unless the user has toggled it manually.
function TraceGroup({ items, isLatest, showAvatar, thinking = false }) {
  const [override, setOverride] = useState(null);
  const open = override === null ? isLatest : override;
  const count = items.length;
  const peek = stripHtml(items[items.length - 1]?.content);
  return (
    <div className={`w-full flex items-start gap-3 ${showAvatar ? 'mt-4' : 'mt-1'}`}>
      {showAvatar ? <AgentAvatar thinking={thinking} /> : <span className="w-8 flex-shrink-0" aria-hidden="true" />}
      <div className="min-w-0 flex-1">
        <button type="button" className="trace-toggle" aria-expanded={open} onClick={() => setOverride(!open)}>
          <svg className={`trace-chevron${open ? ' is-open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
          <span className="flex-shrink-0">{open ? 'Hide activity' : `${count} step${count > 1 ? 's' : ''}`}</span>
          {!open && <span className="trace-toggle-peek">· {peek}</span>}
        </button>
        {open && (
          <div className="trace-list">
            {items.map(it => (
              <div key={it.id} className="trace-line" dangerouslySetInnerHTML={{ __html: mdToHtml(it.content) }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Frozen job-matching/RDQ card inline in the transcript — the reviewable
// record of a proxy task once it's approved, or once a newer task for the
// same job supersedes it while it was still pending. "Review" reopens the
// full ProxyTaskPanel read-only against exactly this snapshot.
function ProxyTaskSnapshotCard({ task, approved, onReview }: { task: ProxyTask; approved?: boolean; onReview: () => void }) {
  const label = PROXY_WORKFLOW_REGISTRY[task.workflowType]?.label || task.workflowType;
  return (
    <div className="result-card">
      <div className="flex items-center gap-2.5 mb-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-ink truncate">{task.title}</div>
          <div className="text-xs text-muted">{label} · {task.subtitle}</div>
        </div>
        <span className={`tp tp-px ${approved ? '' : 'opacity-70'}`}>{approved ? 'Approved' : 'Superseded'}</span>
      </div>
      <button onClick={onReview} className="text-xs font-semibold text-[#2563EB]">Review</button>
    </div>
  );
}

function ResultCard({ job }) {
  return (
    <div className="result-card">
      <div className="flex items-center gap-2.5 mb-3">
        <div>
          <div className="text-sm font-semibold text-ink">Job #{job.jobNum} — {job.client}</div>
          <div className="text-xs text-muted">{job.type} · event {job.eventDate}</div>
        </div>
        <span className="tp tp-px ml-auto">{job.type}</span>
      </div>
      <div className="flex gap-6 flex-wrap mb-3">
        <div><div className="text-xs text-muted uppercase tracking-wide mb-1">Issuer</div><div className="text-sm font-medium text-[#0F6E56]">{job.client}</div></div>
        <div><div className="text-xs text-muted uppercase tracking-wide mb-1">Event date</div><div className="text-sm font-medium text-[#0F6E56]">{job.eventDate}</div></div>
        <div><div className="text-xs text-muted uppercase tracking-wide mb-1">Material</div><div className="text-sm font-medium text-[#0F6E56]">{job.material}</div></div>
      </div>
    </div>
  );
}


// Escalation dialog — note + (for team escalation) a target-team select.
function EscalateModal({ mode, onClose, onSubmit }: { mode: 'role' | 'reorder'; onClose: () => void; onSubmit: (note: string, teamId: string) => void }) {
  const [note, setNote] = useState('');
  const [teamId, setTeamId] = useState<string>(ESCALATION_TARGETS[0].id);
  const title = mode === 'role' ? 'Escalate to another team' : 'Escalate for reorder';
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(4,50,67,.5)' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-modal w-[460px] overflow-hidden">
        <div className="px-5 py-4 border-b border-trim flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ink">{title}</h3>
          <button onClick={onClose} className="text-muted hover:text-ink text-lg leading-none">×</button>
        </div>
        <div className="px-5 py-4 space-y-3">
          {mode === 'role' && (
            <div>
              <label className="text-xs font-semibold text-muted uppercase tracking-wide">Assign to team</label>
              <select className="w-full px-3 py-2 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white mt-1" value={teamId} onChange={e => setTeamId(e.target.value)}>
                {ESCALATION_TARGETS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-muted uppercase tracking-wide">Note</label>
            <textarea className="w-full px-3 py-2 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white mt-1 resize-none" rows={3} autoFocus placeholder={mode === 'role' ? 'Why are you escalating? What should they check?' : 'Short note for the reorder (shortage details)…'} value={note} onChange={e => setNote(e.target.value)} />
          </div>
        </div>
        <div className="px-5 py-3 bg-slate-50 border-t border-trim flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 rounded-lg text-sm border border-trim text-copy hover:bg-white">Cancel</button>
          <button onClick={() => onSubmit(note.trim(), teamId)} className="px-3 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-cyan-500 hover:shadow-lg">{mode === 'role' ? 'Escalate' : 'Route to reorder'}</button>
        </div>
      </div>
    </div>
  );
}

// Picks the arriving reorder receipt to merge into the original ticket.
function MergePickerModal({ targetId, onClose, onPick }: { targetId: string; onClose: () => void; onPick: (sourceId: string) => void }) {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    getMaterialReceipts()
      .then(all => setRows(all.filter(r =>
        r.id !== targetId && r.material_status !== 'Matched' && r.material_status !== 'Follow Ups' && !r.reorder_of_id,
      )))
      .finally(() => setLoading(false));
  }, [targetId]);
  const docSummary = (r: any) => Object.entries(r.received_docs || {}).map(([t, q]) => `${t} ×${q}`).join(', ') || '—';
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(4,50,67,.5)' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-modal w-[520px] max-h-[80vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-trim flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ink">Merge arriving material</h3>
          <button onClick={onClose} className="text-muted hover:text-ink text-lg leading-none">×</button>
        </div>
        <div className="px-5 py-3 text-xs text-muted">Pick the newly-arrived reorder receipt. Its received quantities are added to this ticket.</div>
        <div className="px-5 pb-4 overflow-y-auto flex flex-col gap-2">
          {loading && <div className="text-sm text-muted">Loading…</div>}
          {!loading && rows.length === 0 && <div className="text-sm text-muted">No candidate receipts. Scan the arriving batch first.</div>}
          {rows.map(r => (
            <button key={r.id} onClick={() => onPick(r.id)} className="text-left p-3 rounded-xl border border-trim hover:bg-tint hover:border-teal-300 transition-all">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-ink">{r.scan_number}</span>
                <span className="text-xs text-muted">{(r.client_names || []).join(', ')}</span>
              </div>
              <div className="text-xs text-copy mt-0.5">{docSummary(r)}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ChatMessage({ msg, draft, firstInGroup = true, lastInGroup = true, avatarThinking = false, onQaClick, onConfirmMatch, onNoJobFound, onReceivedShort, onHold, onReviewEdit, onViewPdf, onOpenAllocation, onReviewProxyTaskSnapshot }) {
  const isUser = msg.role === 'user';
  const bubbleRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const timeLabel = msg.ts ? relTime(msg.ts) : (msg.time || '');
  const timeTip = msg.ts ? utcStr(msg.ts) : (msg.time || '');
  const hasExtras = (msg.showResultsCard && draft) || msg.card || msg.proxyTaskSnapshot || (msg.actions && msg.actions.length > 0);

  const handleCopy = () => {
    const text = bubbleRef.current?.innerText || '';
    navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div className={`chat-turn w-full flex flex-col ${isUser ? 'items-end' : 'items-start'} ${firstInGroup ? 'mt-4' : 'mt-0.5'}`}>

      {/* Avatar + bubble — avatar shows only on the first message of a same-sender run */}
      <div className={`flex items-start gap-3 max-w-[640px] ${isUser ? 'flex-row-reverse' : ''}`}>
        {!firstInGroup ? (
          <span className="w-8 flex-shrink-0" aria-hidden="true" />
        ) : isUser ? (
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0 mt-0.5"
            style={{ background: 'linear-gradient(135deg, var(--onx-teal), var(--onx-purple))' }}
          >
            {msg.av || 'U'}
          </div>
        ) : (
          <AgentAvatar thinking={avatarThinking} />
        )}
        <div
          ref={bubbleRef}
          className={`text-sm leading-relaxed min-w-0 ${isUser ? 'chat-bubble-user px-3 py-2' : 'chat-bubble-agent pt-1'}`}
          dangerouslySetInnerHTML={{ __html: isUser ? msg.content : mdToHtml(msg.content) }}
        />
      </div>

      {/* Extras (cards / forms / quick actions) — aligned under the bubble */}
      {hasExtras && (
        <div className={isUser ? 'w-full flex flex-col items-end' : 'w-full pl-11'}>
          {msg.showResultsCard && draft && (
            <div className="mt-2 w-full max-w-lg">
              <MatchResultsCard
                draft={draft}
                onConfirm={() => onConfirmMatch(msg.receiptId)}
                onNoJobFound={() => onNoJobFound(msg.receiptId)}
                onReceivedShort={() => onReceivedShort(msg.receiptId)}
                onHold={(reason) => onHold(msg.receiptId, reason)}
                onReviewEdit={() => onReviewEdit(msg.receiptId)}
                onViewPdf={onViewPdf}
                onOpenAllocation={() => onOpenAllocation(msg.receiptId)}
              />
            </div>
          )}
          {msg.card && (
            <div className="mt-1 w-full max-w-sm"><ResultCard job={msg.card} /></div>
          )}
          {msg.proxyTaskSnapshot && (
            <div className="mt-1 w-full max-w-sm">
              <ProxyTaskSnapshotCard
                task={msg.proxyTaskSnapshot}
                approved={msg.proxyTaskSnapshotApproved}
                onReview={() => onReviewProxyTaskSnapshot(msg.proxyTaskSnapshot, msg.proxyTaskSnapshotApproved)}
              />
            </div>
          )}
          <ChatActions actions={msg.actions} onAction={onQaClick} />
        </div>
      )}

      {/* Hover-revealed action row (last message of a same-sender group only) */}
      {lastInGroup && (
      <div className={`bubble-actions ${isUser ? 'user-actions' : ''}`}>
        {isUser ? (
          <>
            <button className="btn-icon-sm" title="Copy" onClick={handleCopy}>{copied ? <CheckIcon /> : <CopyIcon />}</button>
            <span className="bubble-actions-time" title={timeTip}>{timeLabel}</span>
          </>
        ) : (
          <>
            <span className="bubble-actions-time" title={timeTip}>{timeLabel}</span>
            <button className="btn-icon-sm" title="Copy" onClick={handleCopy}>{copied ? <CheckIcon /> : <CopyIcon />}</button>
          </>
        )}
      </div>
      )}
    </div>
  );
}

export function AcePage() {
  const { setTopbar, setQuickActions, activeSection } = useAppShell();
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const outletCtx = (useOutletContext() as any) || {};
  const { showToast } = outletCtx;
  const { sessions, activeId, msgs, msgsById, ensureSession, newSession, updateSession, appendMsg, replaceMsgs, setActive } = useChats();
  const activeSess = sessions.find(s => s.id === activeId);
  const { data: floorState, loading: floorLoading } = useFloorState();
  const { jobs: proxyJobs, appendFollowUp: proxyAppendFollowUp } = useProxyData();
  const { proxyEmails, threads: emailThreads } = useEmailInbox();
  const proxyWorkflowCtx = useProxyWorkflowContext();
  const { send: sendTeamMessage } = useTeamMessages();
  // Read at click time by message actions, which outlive the render that made them.
  const proxyCtxRef = useRef(proxyWorkflowCtx);
  proxyCtxRef.current = proxyWorkflowCtx;

  const [input, setInput]           = useState('');
  const [isLoading, setIsLoading]         = useState(false);
  const [camOpen, setCamOpen]             = useState(false);
  const [pendingUpload, setPendingUpload] = useState(false);
  // Per-receipt editable drafts (the card reads them read-only; the drawer edits).
  // Persisted to localStorage so the match card survives a chat-session reload.
  const [drafts, setDrafts]                   = useState<Record<string, ReceiptDraft>>(() => {
    try { return JSON.parse(localStorage.getItem('opsai.receiptdrafts.v1') || '{}'); } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem('opsai.receiptdrafts.v1', JSON.stringify(drafts)); } catch { /* quota / serialization — non-fatal */ }
  }, [drafts]);
  const [drawerReceiptId, setDrawerReceiptId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen]           = useState(false);
  const [allocPanelOpen, setAllocPanelOpen]   = useState(false);
  const [allocTarget, setAllocTarget]         = useState(null);
  const [pdfPanelOpen, setPdfPanelOpen]       = useState(false);
  const [pdfTarget, setPdfTarget]             = useState<{ fileUri?: string; scan?: string } | null>(null);
  const [escalateTarget, setEscalateTarget]   = useState<{ receiptId: string; mode: 'role' | 'reorder' } | null>(null);
  const [mergeTarget, setMergeTarget]         = useState<string | null>(null);
  const [reviewingSnapshot, setReviewingSnapshot] = useState<{ task: ProxyTask; approved?: boolean } | null>(null);
  // Whether the right-hand ProxyTaskPanel should show for the active session
  // — driven by which queue/entry point opened it, NOT by anything stored on
  // the (possibly job-merged, multi-role) session itself. A job's thread can
  // carry both a Fulfillment receiptId and a Proxy proxyTask at once; without
  // this, opening the SAME merged thread from Fulfillment's side would also
  // show Proxy's job-matching panel, which only belongs on the Proxy side.
  const [proxyPanelVisible, setProxyPanelVisible] = useState(false);
  const [displayText, setDisplayText] = useState('');
  const [panelWidth, setPanelWidth] = useState(307);
  const fileInputRef = useRef(null);
  const chatEndRef  = useRef(null);
  const lastHandledNavKey = useRef(null);
  const typeRef = useRef({ msgIdx: 0, charIdx: 0 });
  const isResizing = useRef(false);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);

  const TYPEWRITER_MSGS = activeSection === 'proxy' ? [
    "I'm Ace. How can I help you today?",
    "Ask me about a job, a solicitor, or a contest...",
    "Need to log an inbound email?",
    "I can draft follow-ups, calculate RDQs, and track contests.",
  ] : [
    "I'm Ace. How can I help you today?",
    "Ask me about jobs or materials...",
    "Need help taking a material scan?",
    "I can assist with a range of operations.",
  ];

  const AVATAR_COLOR = '#1ABC9D';
  const getInitials = (name = '') =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const now = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  useEffect(() => {
    setTopbar('');
  }, [setTopbar]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isResizing.current) return;
      const delta = resizeStartX.current - e.clientX;
      setPanelWidth(Math.min(Math.max(resizeStartWidth.current + delta, 200), 900));
    };
    const onMouseUp = () => {
      if (!isResizing.current) return;
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  useEffect(() => {
    if (activeSess?.kind === 'email' || activeSess?.kind === 'advisor-email' || (proxyPanelVisible && activeSess?.proxyTask)) {
      setPanelWidth(Math.floor(window.innerWidth * 0.48));
    } else if (activeSess) {
      setPanelWidth(307);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSess?.id, activeSess?.kind, proxyPanelVisible]);

  useEffect(() => {
    // Skip fallback session if location.state will create one via the handler below
    if (!activeId && !location.state) newSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle switching sessions when navigating back from /history
  useEffect(() => {
    if (location.state?.chatSessionId) {
      const sid = location.state.chatSessionId;
      window.history.replaceState({}, '');
      setActive(sid);
      // History doesn't carry a "which queue" origin — fall back to whatever
      // this specific session was originally created as.
      setProxyPanelVisible(sessions.find(s => s.id === sid)?.kind === 'proxy-task');
    }
  }, [location.state, setActive, sessions]);

  // Handle incoming thread context from queue pages
  useEffect(() => {
    // React Router may keep this page mounted across navigations.
    // Handle each navigation state payload once per location.key.
    if (lastHandledNavKey.current === location.key) return;
    if (!location.state) return;
    lastHandledNavKey.current = location.key;
    // Default off — only openProxyTask (below) turns this back on, so a
    // merged thread's job-matching panel never leaks into a non-Proxy entry.
    setProxyPanelVisible(false);

    if (location.state?.verifyThread) {
      const { client, jobNum } = location.state.verifyThread;
      window.history.replaceState({}, '');
      const sid = openOrReuseJobSession(jobNum, { kind: 'job', jobNum, client });
      setTimeout(() => runVerify(client, jobNum, sid), 100);
    } else if (location.state?.chaseThread) {
      const { client, jobNum } = location.state.chaseThread;
      window.history.replaceState({}, '');
      const sid = openOrReuseJobSession(jobNum, { kind: 'job', jobNum, client });
      setTimeout(() => runChase(client, jobNum, sid), 100);
    } else if (location.state?.caEmail) {
      const email = location.state.caEmail;
      window.history.replaceState({}, '');
      const sid = newSession({ kind: 'email', client: email.companyName, caEmail: email });
      setTimeout(() => {
        addUserMsgTo(sid,
          `Process this advisor email:\n\n**From:** ${email.from} <${email.fromEmail}>\n**Subject:** ${email.subject}\n\n${email.body}`
        );
      }, 50);
    } else if (location.state?.advisorEmail) {
      const email = location.state.advisorEmail;
      window.history.replaceState({}, '');
      const sid = newSession({ kind: 'advisor-email', client: email.clientName, advisorEmail: email });
      setTimeout(() => {
        addUserMsgTo(sid,
          `Process this registration change request:\n\n**From:** ${email.from} <${email.fromEmail}>\n**Subject:** ${email.subject}\n**Account:** ${email.accountNumber}\n\n${email.body}`
        );
      }, 50);
    } else if (location.state?.receiptThread) {
      const rt = location.state.receiptThread;
      window.history.replaceState({}, '');
      openReceiptThread(rt);
    } else if (location.state?.proxyTask) {
      const task = location.state.proxyTask as ProxyTask;
      window.history.replaceState({}, '');
      openProxyTask(task);
    } else if (location.state?.triggerUpload) {
      window.history.replaceState({}, '');
      setPendingUpload(true);
    } else if (location.state?.triggerCamera) {
      window.history.replaceState({}, '');
      setCamOpen(true);
    }
  }, [location.key, location.state, newSession]);

  // Typewriter effect for welcome state
  useEffect(() => {
    let timer;
    const step = () => {
      const { msgIdx, charIdx } = typeRef.current;
      const msg = TYPEWRITER_MSGS[msgIdx];
      if (charIdx <= msg.length) {
        setDisplayText(msg.slice(0, charIdx));
        typeRef.current.charIdx++;
        timer = setTimeout(step, 38);
      } else {
        timer = setTimeout(() => {
          typeRef.current = { msgIdx: (msgIdx + 1) % TYPEWRITER_MSGS.length, charIdx: 0 };
          step();
        }, 2200);
      }
    };
    step();
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pendingUpload) { setPendingUpload(false); triggerUpload(); }
  }, [pendingUpload]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs, isLoading]);

  const addMsg = useCallback((content, role, extras = {}) => {
    const sid = activeId || ensureSession();
    appendMsg(sid, { id: String(Date.now() + Math.random()), role, content, time: now(), ...extras });
  }, [activeId, ensureSession, appendMsg]);

  const addAgentMsg = useCallback((content, actions = [], extras = {}) => {
    const sid = activeId || ensureSession();
    appendMsg(sid, { id: String(Date.now() + Math.random()), role: 'agent', content, actions: actions || [], time: now(), ...extras });
  }, [activeId, ensureSession, appendMsg]);

  const addAgentMsgTo = useCallback((sid, content, actions = [], extras = {}) => {
    if (!sid) return;
    appendMsg(sid, { id: String(Date.now() + Math.random()), role: 'agent', content, actions: actions || [], time: now(), ...extras });
  }, [appendMsg]);

  const addUserMsg = useCallback((text) => {
    const sid = activeId || ensureSession();
    appendMsg(sid, {
      id: String(Date.now() + Math.random()), role: 'user', content: text, time: now(),
      sender: user?.name?.split(' ')[0] || 'User',
      av: getInitials(user?.name), avatarColor: AVATAR_COLOR,
    });
  }, [user, activeId, ensureSession, appendMsg]);

  const addUserMsgTo = useCallback((sid, text) => {
    if (!sid) return;
    appendMsg(sid, {
      id: String(Date.now() + Math.random()), role: 'user', content: text, time: now(),
      sender: user?.name?.split(' ')[0] || 'User',
      av: getInitials(user?.name), avatarColor: AVATAR_COLOR,
    });
  }, [user, appendMsg]);

  const triggerUpload = useCallback(() => {
    newSession({ kind: 'upload', title: 'Scan upload' });
    fileInputRef.current?.click();
  }, [newSession]);

  const uploadAndMatch = useCallback(async (file: File, label: string, category: string = 'P') => {
    showTyping();
    try {
      const { upload_id } = await uploadPhoto(file);
      const poll = setInterval(async () => {
        try {
          const status = await getMatchStatus(upload_id);
          if (status.status === 'completed') {
            clearInterval(poll);
            hideTyping();
            const result = await getMatchResults(upload_id);
            window.currentMatchResult = result;
            const r = result as any;
            addAgentMsg(
              `Extracted from <strong>${label}</strong>:<br><br>
              <div style="background:#E9F6F6;border-radius:8px;padding:12px;display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:4px">
                <div><div style="font-size:11px;color:#73747A;font-weight:600;text-transform:uppercase;letter-spacing:.06em">Issuer</div><div style="font-weight:600;color:#043243;margin-top:2px">${(r.scanInfo?.issuerNames ?? []).join(' / ') || '—'}</div></div>
                <div><div style="font-size:11px;color:#73747A;font-weight:600;text-transform:uppercase;letter-spacing:.06em">Doc type</div><div style="font-weight:500;margin-top:2px">${r.scanInfo?.materials?.[0]?.type || '—'}</div></div>
                <div><div style="font-size:11px;color:#73747A;font-weight:600;text-transform:uppercase;letter-spacing:.06em">Event date</div><div style="font-weight:500;margin-top:2px">${r.scanInfo?.eventDate || '—'}</div></div>
                <div><div style="font-size:11px;color:#73747A;font-weight:600;text-transform:uppercase;letter-spacing:.06em">Pages</div><div style="font-weight:500;margin-top:2px">${r.pageMatch?.totalPages || '—'}</div></div>
              </div><br>Does this look right? I'll search MIC for a matching job.`,
              [{ label: 'View match results', fn: () => runMatch(result, category) }, { label: 'Correct a field', fn: () => addAgentMsg('Which field needs fixing — issuer, event date, or doc type?') }]
            );
          } else if (status.status === 'error') {
            clearInterval(poll);
            hideTyping();
            addAgentMsg(`There was an error processing <strong>${label}</strong>. Please try again.`);
          }
        } catch {
          clearInterval(poll);
          hideTyping();
          addAgentMsg(`There was an error processing <strong>${label}</strong>. Please try again.`);
        }
      }, 1500);
    } catch (err: any) {
      hideTyping();
      addAgentMsg(`Upload failed: ${err?.message || 'unknown error'}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addAgentMsg]);

  const handleFilesSelected = useCallback((e) => {
    const files = Array.from(e.target.files || []) as File[];
    e.target.value = '';
    if (files.length === 0) return;
    const label = files.length === 1 ? files[0].name : `${files.length} files`;
    if (activeId) updateSession(activeId, { title: label });
    showToast?.(`${label} uploading…`);
    uploadAndMatch(files[0], label);
  }, [activeId, updateSession, showToast, uploadAndMatch]);

  // Just open the camera — the session is created on Find Job (always fresh).
  const openCameraCapture = useCallback(() => { setCamOpen(true); }, []);

  // Floor-state hero one-tap action: deep-link if the user can see the page,
  // otherwise fall back to asking Ace directly.
  const handleFloorAction = useCallback((card: FloorCard) => {
    trackEvent('floor_action_click', { key: card.key });
    const { kind, route } = card.action;
    if (kind === 'view' && route) {
      const pageId = PAGE_PATHS.find(p => p.path === route)?.id;
      if (pageId && !pageVisible(user, pageId)) return;
      navigate(route);
      return;
    }
    sendText(`Tell me more about ${card.fact.toLowerCase()}.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, user]);

  useEffect(() => {
    setQuickActions({ camera: openCameraCapture, upload: triggerUpload, jobTag: null });
    return () => setQuickActions({ camera: null, upload: null, jobTag: null });
  }, [setQuickActions, openCameraCapture, triggerUpload]);

  const showTyping = () => setIsLoading(true);
  const hideTyping = () => setIsLoading(false);

  function agentAfter(ms, fn) { showTyping(); setTimeout(() => { hideTyping(); fn(); }, ms); }

  // triggerUpload + handleFilesSelected moved above and memoized for responsiveness

  // Scan-number convention: Proxy = S-P<n>, Corp Action = S-R<n>, Issuer = S-I<n>;
  // Regulatory has no letter prefix — MMDDYY + 2-digit order of processing.
  function generateScanNumber(category: string) {
    if (category === 'R') {
      const now = new Date();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      const yy = String(now.getFullYear()).slice(-2);
      const order = String(1 + Math.floor(Math.random() * 99)).padStart(2, '0');
      return `${mm}${dd}${yy}${order}`;
    }
    const prefix = category === 'CA' ? 'S-R' : category === 'I' ? 'S-I' : 'S-P';
    return `${prefix}${100 + Math.floor(Math.random() * 900)}`;
  }

  function runMatch(matchResult, category = 'P') {
    const result = matchResult || window.currentMatchResult;

    addUserMsg('View match results');
    setTimeout(() => {
      hideTyping();
      addAgentMsg(`Here are the extracted fields for <strong>${result.scanInfo.filename}</strong>. Look up the job in MIC and enter it on the right — I'll flag the routing.`, [], {
        showResultsCard: true,
        matchResult: result,
        matchCategory: category,
      });
    }, 500);
  }

  // Seed/refresh the editable draft for a receipt (the card + drawer read it).
  function seedDraft(row: any) {
    if (row?.id) setDrafts(prev => ({ ...prev, [row.id]: draftFromRow(row) }));
  }
  const updateDraft = (receiptId: string, patch: Partial<ReceiptDraft>) =>
    setDrafts(prev => (prev[receiptId] ? { ...prev, [receiptId]: { ...prev[receiptId], ...patch } } : prev));

  // Present the read-only matching card inside a thread (draft seeded from the row).
  function presentReceiptCard(sid: string, row: any, category: string, extraActions: any[] = []) {
    seedDraft(row);
    addAgentMsgTo(sid,
      `Extraction complete for <code>${row.scan_number}.pdf</code> — now ${statusPillHtml(row.material_status || 'To Verify')}. ` +
      `Open <strong>Review / edit</strong> on the card to amend the fields and enter the matching job — I'll flag the routing.`,
      extraActions,
      { showResultsCard: true, receiptId: row.id, matchCategory: category, cardStatus: row.material_status || 'To Verify' },
    );
  }

  // Statuses with a dedicated Resolve / Escalate flow (vs. a fresh intake card).
  const EXCEPTION_STATUSES = ['No Job Found', 'Received Short', 'On Hold', 'Follow Ups'];

  // Re-run the extraction pipeline against an existing receipt: New/On Hold →
  // Processing → To Verify, then present the match card. Shared by the camera
  // intake flow and the On-Hold "Retry extraction" path.
  function runExtractionPipeline(sid: string, receiptId: string, category: string, extraActions: any[] = [], fallbackRow: any = null) {
    setTimeout(() => { updateMaterialReceipt(receiptId, { material_status: 'Processing' }).catch(() => {}); }, 1200);
    setTimeout(async () => {
      let verified = fallbackRow;
      try { verified = await updateMaterialReceipt(receiptId, { material_status: 'To Verify' }); } catch {}
      hideTyping();
      if (verified) presentReceiptCard(sid, verified, category, extraActions);
      else addAgentMsgTo(sid, 'Could not complete extraction — please retry or escalate.');
    }, 3000);
  }

  // Present the type-aware Resolve / Escalate card for an exception receipt.
  function presentExceptionCard(sid: string, row: any, category: string) {
    seedDraft(row);
    const status = row.material_status;
    trackEvent('contextual_prompt_shown', { receiptId: row.id, status });
    const askAceAction = {
      label: 'Ask Ace why',
      fn: () => {
        trackEvent('contextual_prompt_accepted', { receiptId: row.id, status });
        sendText(`Why was ${row.scan_number} flagged as ${status}?`);
      },
    };
    if (status === 'On Hold') {
      addAgentMsgTo(sid,
        `This scan is ${statusPillHtml('On Hold')} — the extraction pipeline didn't complete. ` +
        `Retry extraction to re-run it, then verify and match.`,
        [...(can('diagnostics') ? [{ label: 'Retry extraction', fn: () => handleRetryExtraction(row.id) }] : []), askAceAction],
        { receiptId: row.id, cardStatus: status },
      );
      return;
    }
    if (status === 'Follow Ups') {
      addAgentMsgTo(sid,
        `This ticket is ${statusPillHtml('Follow Ups')} — waiting on a reorder batch. ` +
        `When the new material arrives, merge it in to combine quantities, then match.`,
        // Merge is a Manager verb — operators see the state but route the
        // merge to their manager.
        [...(can('receipt.merge') ? [{ label: 'Merge arriving material', fn: () => handleMergeReorder(row.id) }] : []), askAceAction],
        { receiptId: row.id, cardStatus: status },
      );
      return;
    }
    if (status === 'Received Short') {
      // Already auto-escalated to Cary at disposition, and Ace has made the
      // ICT-13 shortage follow-up a task in Proxy's Intake Queue. One place to
      // work it, the same review card as every other Proxy task.
      const followAction = [{ label: 'Open the shortage task', fn: () => openShortageTask(sid, row) }, askAceAction];
      addAgentMsgTo(sid,
        `This scan is ${statusPillHtml('Received Short')}. It's logged to the Receiving Log and escalated to <strong>${teamLabel('cary')}</strong>, ` +
        `and Ace has a shortage follow-up ready for them in the Intake Queue (ICT-13).`,
        followAction,
        { showResultsCard: true, receiptId: row.id, matchCategory: category, cardStatus: status },
      );
      return;
    }
    // No Job Found → resolve via the match card, or escalate to another team.
    const escalateAction = { label: 'Escalate to team', fn: () => handleEscalateToRole(row.id) };
    const guidance = 'Find the correct job, enter it under <strong>Review / edit</strong>, then Resolve &amp; Match — or escalate to another team.';
    addAgentMsgTo(sid,
      `This scan is ${statusPillHtml(status)}. ${guidance}`,
      [...(can('receipt.escalate') ? [escalateAction] : []), askAceAction],
      { showResultsCard: true, receiptId: row.id, matchCategory: category, cardStatus: status },
    );
  }

  // Overs review card — the receipt's job completed with excess stock. Wayne
  // confirms the excess amount + where it's stored (Location), then marks it stored.
  function presentOversCard(sid: string, row: any, category: string) {
    seedDraft(row);
    const detail = oversSummary(drafts[row.id] || draftFromRow(row));
    addAgentMsgTo(sid,
      `This receipt has <strong>excess stock (Overs)</strong> pending review${detail ? ` — ${detail}` : ''}. ` +
      `Confirm where the excess is stored (set the <strong>Location</strong> under Review / edit), then mark it stored.`,
      can('receipt.edit')
        ? [{ label: 'Review / edit', fn: () => handleReviewEdit(row.id) }, { label: 'Confirm stored', fn: () => handleConfirmOvers(row.id) }]
        : [],
      { receiptId: row.id, cardStatus: 'Overs-Pending' },
    );
  }

  // Server thread message → local chat message shape (extras spread back in).
  const threadMsgToLocal = (m: any) => ({ id: m.id, role: m.role, content: m.content, ts: m.created_at, ...(m.extras || {}) });

  // Was the thread's last entry already the resolution/match card for this
  // receipt in its current state? Prevents one-card-per-open log spam.
  const lastIsCurrentCard = (list: any[], row: any) => {
    const last = list[list.length - 1];
    return !!last && last.receiptId === row.id && last.cardStatus === row.material_status;
  };

  // One-time backfill: threads created before server persistence become shared.
  // Sequential to preserve order; bails on first failure (retried on next open).
  async function backfillThread(receiptId: string, localMsgs: any[]) {
    for (const m of localMsgs) {
      try { await postReceiptThreadMessage(receiptId, toThreadPayload(m)); }
      catch { return; }
    }
  }

  // Merge-by-job-number: the same job's conversation is one thread regardless
  // of which side (Fulfillment receipt, Proxy task, verify/chase) opened it.
  // Matches on overlap against every job number a session has ever touched —
  // a receipt can carry more than one job number, and there's no guarantee
  // which one a session was first created against.
  function findJobSession(jobNums: string[]): ChatSessionLocal | undefined {
    if (!jobNums.length) return undefined;
    return sessions.find(s =>
      (s.jobNum && jobNums.includes(s.jobNum)) ||
      s.jobNums?.some(j => jobNums.includes(j))
    );
  }

  function unionJobNums(existing: ChatSessionLocal, jobNums: string[]): string[] {
    return Array.from(new Set([
      ...(existing.jobNum ? [existing.jobNum] : []),
      ...(existing.jobNums || []),
      ...jobNums,
    ]));
  }

  // Reuses the job's existing thread when one exists; otherwise creates it.
  function openOrReuseJobSession(jobNum: string | undefined, meta: Partial<ChatSessionLocal>): string {
    const jobNums = jobNum ? [jobNum] : [];
    const existing = findJobSession(jobNums);
    if (existing) {
      updateSession(existing.id, { ...meta, jobNums: unionJobNums(existing, jobNums) });
      setActive(existing.id);
      return existing.id;
    }
    return newSession({ ...meta, jobNum, jobNums });
  }

  // Seeds the left transcript for a proxy task's thread the first time it's
  // opened with no history yet — Fulfillment's own original job-matching
  // card for this material (same MatchResultsCard/Review-edit treatment its
  // own thread would show), then the inbound email/escalation Ace read, then
  // Ace's own framing. Without this, a brand-new thread has zero messages
  // and the left column falls back to the generic idle "Welcome back"
  // cockpit even though the right panel is showing a real, job-specific card.
  function presentProxyTaskIntro(sid: string, task: ProxyTask) {
    if ((msgsById[sid] || []).length > 0) return;

    const row = proxyWorkflowCtx.receipts?.find(r =>
      r.job_numbers?.includes(task.jobId) || r.scan_number === task.jobId);
    if (row) {
      seedDraft(row);
      const category = row.job_category || 'P';
      if (row.overs_status === 'Pending') presentOversCard(sid, row, category);
      else if (EXCEPTION_STATUSES.includes(row.material_status)) presentExceptionCard(sid, row, category);
      else presentReceiptCard(sid, row, category);
    }

    const email = task.source === 'email' ? proxyEmails.find(e => e.id === task.sourceEmailId) : undefined;
    if (email) {
      appendMsg(sid, {
        id: String(Date.now() + Math.random()),
        role: 'user',
        content: `Process this inbound email:<br><br><strong>From:</strong> ${email.from} &lt;${email.fromEmail}&gt;<br><strong>Subject:</strong> ${email.subject}<br><br>${email.body.replace(/\n/g, '<br>')}`,
      });
    } else if (task.sourceDetail) {
      appendMsg(sid, {
        id: String(Date.now() + Math.random()),
        role: 'user',
        content: `Process this escalation:<br><br>${task.sourceDetail.rows.map(r => `<strong>${r.label}:</strong> ${r.value}`).join('<br>')}`,
      });
    }

    const workflow = PROXY_WORKFLOW_REGISTRY[task.workflowType];
    addAgentMsgTo(sid, `<b>Ace:</b> ${task.reason} I've prepared ${workflow?.noun || 'a review'} on the right — take a look.`);
  }

  // Open the ACE workflow panel for a Proxy Intake Queue task — mirrors the
  // caEmail/advisorEmail pattern below, one session per job, panel dispatched
  // by the registered workflowType (see data/proxyWorkflowRegistry.ts). A job
  // can accumulate more than one task over its life (e.g. an RDQ order, later
  // a shortage follow-up); reopening the job's thread instead of starting a
  // new one keeps that whole history — and any Fulfillment receipt thread for
  // the same job — together.
  function openProxyTask(task: ProxyTask) {
    const job = proxyJobs.find(j => j.jobId === task.jobId);
    const jobNums = [task.jobId];
    const existing = findJobSession(jobNums);
    if (existing) {
      const isSameTask = existing.proxyTask?.id === task.id;
      // A different, still-pending task is about to be replaced — freeze it
      // into the transcript first so it stays reviewable. An already-approved
      // task was already frozen at approval time (ProxyTaskPanel.handleApprove).
      if (existing.proxyTask && !isSameTask && !existing.proxyTaskApproved) {
        appendMsg(existing.id, {
          id: String(Date.now() + Math.random()),
          role: 'agent',
          content: `Superseded — a new task came in for Job #${task.jobId} before this one was actioned.`,
          proxyTaskSnapshot: existing.proxyTask,
          proxyTaskSnapshotApproved: false,
        });
      }
      // Never overwrite `kind` here — the session may be Fulfillment-
      // originated (kind: 'camera'), and kind is what the History page and
      // other entry points use to identify a thread's own type.
      updateSession(existing.id, {
        proxyTask: task,
        // Only open tasks reach here, so an approved session for the same
        // task id means it's open again (a restarted tour, a contest's next
        // stage): show it as not yet approved.
        ...(isSameTask && !existing.proxyTaskApproved ? {} : { proxyTaskApproved: false }),
        client: job?.issuer,
        jobNum: task.jobId,
        jobNums: unionJobNums(existing, jobNums),
      });
      setActive(existing.id);
      setProxyPanelVisible(true);
      return;
    }
    newSession({ kind: 'proxy-task', client: job?.issuer, proxyTask: task, jobNum: task.jobId, jobNums });
    setProxyPanelVisible(true);
  }

  // presentProxyTaskIntro needs the live receipts fetch (useProxyWorkflowContext)
  // to have resolved to find the original Fulfillment job-matching card — right
  // after a fresh page load that fetch is still in flight, so calling it inline
  // from openProxyTask can race it and silently skip the card. This effect
  // retries once receipts arrives instead of seeding on a best-effort basis.
  useEffect(() => {
    if (!proxyPanelVisible || !activeSess?.proxyTask || !activeId) return;
    if ((msgsById[activeId] || []).length > 0) return;
    if (proxyWorkflowCtx.receipts === null) return; // still loading — effect re-fires once it resolves
    presentProxyTaskIntro(activeId, activeSess.proxyTask);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proxyPanelVisible, activeId, activeSess?.proxyTask, proxyWorkflowCtx.receipts]);

  // Open (or re-open) the thread for a receipt row — used by the queue/log row
  // click. The server thread (one per receipt) is the source of truth — the
  // receipt's shared memory log across users, devices, and workflow stages;
  // localStorage is the cache and the offline fallback.
  async function openReceiptThread(rt: { receiptId: string; scan?: string; client?: string; row?: any }) {
    // Fulfillment's own view never shows Proxy's job-matching panel, even if
    // this thread is merged with a job that also has a pending proxyTask.
    setProxyPanelVisible(false);
    if (rt.row) seedDraft(rt.row);   // refresh draft from the freshly-fetched row
    const row = rt.row;
    const category = row?.job_category || 'P';

    let serverMsgs: any[] | null = null;  // null = server unreachable
    try {
      const t = await getReceiptThread(rt.receiptId);
      serverMsgs = (t.messages || []).map(threadMsgToLocal);
    } catch { /* offline / error — fall back to the local cache */ }

    // Present the receipt's current state into the thread (dedupe-guarded).
    // Exceptions always re-present (Resolve/Escalate actions must be reachable);
    // non-exception receipts only get the card in a thread with no history yet.
    const presentCurrent = (sid: string, history: any[]) => {
      if (!row) return;
      // Overs follow-up (excess stock) is tracked off the material_status, so it
      // gets its own re-presentable review card (deduped on the 'Overs' cardStatus).
      if (row.overs_status === 'Pending') {
        const last = history[history.length - 1];
        if (!(last && last.cardStatus === 'Overs-Pending')) presentOversCard(sid, row, category);
        return;
      }
      if (lastIsCurrentCard(history, row)) return;
      if (EXCEPTION_STATUSES.includes(row.material_status)) presentExceptionCard(sid, row, category);
      else if (history.length === 0) presentReceiptCard(sid, row, category);
    };

    // "No Job Found" receipts carry no job_numbers at all — the scan number
    // itself is the shared identity in that case (UNMATCHED_MATERIAL_ROUTE's
    // synthetic ProxyJob/task uses jobId === scan_number, see
    // data/workflows/unmatchedMaterialWorkflow.ts), so fall back to it rather
    // than leaving those threads unmergeable.
    const jobNums = row?.job_numbers?.length ? row.job_numbers : (row?.scan_number ? [row.scan_number] : []);
    const existing = sessions.find(s => s.receiptId === rt.receiptId) || findJobSession(jobNums);
    if (existing) {
      // Attach this receipt to a job's existing (Proxy-originated) thread, or
      // fold in any job numbers this receipt adds — either way the merged
      // thread becomes the one the receipt syncs to the server under.
      if (existing.receiptId !== rt.receiptId || jobNums.length) {
        updateSession(existing.id, { receiptId: rt.receiptId, scan: rt.scan, jobNums: unionJobNums(existing, jobNums) });
      }
      setActive(existing.id);
      const localMsgs = msgsById[existing.id] || [];
      let history = localMsgs;
      if (serverMsgs && serverMsgs.length === 0 && localMsgs.length > 0) {
        backfillThread(rt.receiptId, localMsgs);  // pre-feature thread → server
      } else if (serverMsgs && serverMsgs.length > 0) {
        replaceMsgs(existing.id, serverMsgs);
        history = serverMsgs;
      }
      setTimeout(() => presentCurrent(existing.id, history), 120);
      return;
    }

    const sid = newSession({
      kind: 'camera', receiptId: rt.receiptId, scan: rt.scan, category,
      client: rt.client, title: `${rt.scan || 'Receipt'} · ${rt.client || ''}`.trim(),
      jobNum: jobNums[0], jobNums,
    });
    const history = serverMsgs || [];
    if (history.length > 0) replaceMsgs(sid, history);  // another user/browser wrote this log
    if (row) setTimeout(() => presentCurrent(sid, history), 120);
    else if (history.length === 0) addAgentMsgTo(sid, 'Loading receipt…');
  }

  // ── Exception resolution handlers ──

  function handleRetryExtraction(receiptId: string) {
    const sid = activeId; if (!sid) return;
    const d = drafts[receiptId];
    const category = d?.category || 'P';
    addUserMsg('Retry extraction');
    addAgentMsg(`Re-running the extraction pipeline for this scan… ${statusPillHtml('Processing')}`, [], { trace: true });
    showTyping();
    runExtractionPipeline(sid, receiptId, category);
  }

  function handleEscalateToRole(receiptId: string) { setEscalateTarget({ receiptId, mode: 'role' }); }
  function handleEscalateReorder(receiptId: string) { setEscalateTarget({ receiptId, mode: 'reorder' }); }
  function handleMergeReorder(receiptId: string) { setMergeTarget(receiptId); }

  // Confirm an Overs follow-up as stored: persist any Location edit + flip
  // overs_status to Completed (then it also surfaces in Inventory ▸ Overs).
  async function handleConfirmOvers(receiptId: string) {
    const d = drafts[receiptId];
    addUserMsg('Confirm stored');
    showTyping();
    try {
      const loc = d?.location?.trim();
      const patch = d ? buildReceiptPatch(d) : {};
      await updateMaterialReceipt(receiptId, {
        ...patch,
        overs_status: 'Completed',
        note_log: [...(d?.noteLog || []), { author: firstName, timestamp: noteStamp(), text: `Overs confirmed stored${loc ? ` at ${loc}` : ''}.`, kind: 'system' }],
      });
      hideTyping();
      addAgentMsg(`Overs confirmed${loc ? ` — stored at <strong>${loc}</strong>` : ''}. It now also shows in Inventory ▸ Overs.`,
        [{ label: 'View Inventory', fn: () => navigate('/inventory') }]);
      showToast?.('Overs confirmed');
    } catch {
      hideTyping();
      addAgentMsg('Could not confirm the overs — please try again.');
    }
  }

  async function submitEscalation(receiptId: string, mode: 'role' | 'reorder', note: string, teamId: string) {
    setEscalateTarget(null);
    const d = drafts[receiptId];
    const baseNotes = d?.noteLog || [];
    showTyping();
    try {
      if (mode === 'role') {
        const label = teamLabel(teamId);
        const text = note || `Escalated to ${label}.`;
        await updateMaterialReceipt(receiptId, {
          escalated_to_team: teamId,
          note_log: [...baseNotes, { author: firstName, timestamp: noteStamp(), text, kind: 'note' }],
        });
        // A notification in the other team's bell, as with every hand-off.
        if (d) {
          sendTeamMessage({
            from: teamId === 'cary' ? 'wayne' : 'cary', to: teamId as 'wayne' | 'cary', kind: 'escalation',
            title: `Escalated: Scan ${d.scan}`, body: text,
            jobId: d.jobs.map(j => j.jobNumber).find(Boolean), scan: d.scan, client: d.clientNames?.[0],
          });
        }
        hideTyping();
        addUserMsg(`Escalate to ${label}`);
        addAgentMsg(`Escalated to <strong>${label}</strong>${note ? ` — <em>${note}</em>` : ''}. It's now in the Escalations queue for that team to resolve.`,
          [{ label: 'View Escalations', fn: () => navigate('/escalations') }]);
        showToast?.(`Escalated to ${label}`);
      } else {
        const text = note || 'Escalated for reorder.';
        await updateMaterialReceipt(receiptId, {
          material_status: 'Follow Ups',
          note_log: [...baseNotes, { author: firstName, timestamp: noteStamp(), text, kind: 'note' }],
        });
        hideTyping();
        addUserMsg('Escalate — reorder');
        addAgentMsg(`Routed to <strong>Follow Ups</strong>${note ? ` — <em>${note}</em>` : ''}. Reorder runs offline; when the new batch arrives, open this ticket and merge it in.`,
          [{ label: 'View Follow Ups', fn: () => navigate('/follow-ups') }]);
        showToast?.('Follow Ups');
      }
    } catch {
      hideTyping();
      addAgentMsg('Could not escalate — please try again.');
    }
  }

  async function submitMerge(targetId: string, sourceId: string) {
    const sid = activeId;
    setMergeTarget(null);
    showTyping();
    try {
      const combined = await mergeReorderReceipt(targetId, sourceId);
      // A Proxy job's missing material is in: tell Proxy so they can close
      // the shortage ticket.
      if ((combined.job_category || 'P') === 'P' && combined.job_numbers?.length) {
        sendTeamMessage({
          from: 'wayne', to: 'cary', kind: 'material-merged',
          title: `Material merged: ${(combined.client_names || [])[0] || combined.scan_number}`,
          body: `The new material for scan ${combined.scan_number} has arrived and been merged into the original receipt.`,
          jobId: combined.job_numbers[0], scan: combined.scan_number, client: (combined.client_names || [])[0],
        });
      }
      hideTyping();
      addUserMsg('Merge arriving material');
      addAgentMsg(`Merged the arriving batch in — quantities combined and the ticket is back to ${statusPillHtml('To Verify')}. Review and confirm the match.`);
      if (sid) presentReceiptCard(sid, combined, combined.job_category || 'P');
      showToast?.('Reorder merged');
    } catch {
      hideTyping();
      addAgentMsg('Could not merge — please try again.');
    }
  }

  function runConfirmMatch() {
    addUserMsg('Confirm match');
    agentAfter(1200, () => {
      addAgentMsg('Done — match confirmed and marked <strong>Received</strong>.', [
        { label: 'Scan another', fn: triggerUpload },
        { label: 'Receiving log', fn: () => navigate('/receiving-log') },
      ]);
      showToast?.('Match confirmed');
    });
  }

  // "Review" on a frozen job-matching/RDQ card in the transcript. If it's
  // still the session's current, unactioned task, the live right-hand panel
  // already shows it — no need to duplicate it in a modal. Otherwise open it
  // read-only, exactly as it looked at that point in the conversation.
  function handleReviewProxyTaskSnapshot(task: ProxyTask, approved?: boolean) {
    if (activeSess?.proxyTask?.id === task.id && !activeSess.proxyTaskApproved) return;
    setReviewingSnapshot({ task, approved });
  }

  // Open the ReceiptDrawer (the editable surface) + the scanned PDF beside it.
  function handleReviewEdit(receiptId: string) {
    setDrawerReceiptId(receiptId);
    setDrawerOpen(true);
    const d = drafts[receiptId];
    if (d?.fileUri) { setPdfTarget({ fileUri: d.fileUri, scan: d.scan }); setPdfPanelOpen(true); }
  }

  // Drawer "Save" → PATCH the receipt's editable columns AND persist the per-job
  // edits (job #, type, status, NAA) from the draft. setMatched=false keeps the
  // receipt in its current state (e.g. an exception) — matching is the separate
  // "Resolve & Match" action — so the drawer stays re-editable.
  // Note: like Receiving Log's Save, an unrecognized job number find-or-creates a
  // JobOrder server-side; we accept that for parity rather than re-diverging the
  // two Save paths.
  async function handleDrawerSave() {
    if (!drawerReceiptId) return;
    const d = drafts[drawerReceiptId];
    if (!d) return;
    try {
      await updateMaterialReceipt(drawerReceiptId, buildReceiptPatch(d));
      const jobs = draftToMatchJobs(d);
      if (jobs.length) await confirmReceiptMatch(drawerReceiptId, jobs, false);
      showToast?.('Receipt saved');
    } catch (err) {
      console.error('Drawer save failed:', err);
      const status = (err as any)?.response?.status;
      showToast?.(status ? `Failed to save receipt (server ${status})` : 'Failed to save receipt — is the backend reachable?');
    }
  }

  // Open the multi-job allocation drill-down, seeded from the draft.
  function handleOpenAllocation(receiptId: string) {
    const d = drafts[receiptId];
    if (!d) return;
    const matchedJobs = d.jobs.map((j, i) => ({
      jobId: j.jobNumber || `job-${i + 1}`,
      issuerName: d.clientNames[0] || '',
      eventDate: d.eventDate,
      materials: d.receivedDocs.map(doc => ({ type: doc.type, orderedQty: orderedFor(d, doc.type) })),
    }));
    setAllocTarget({ result: { matchedJobs, scanInfo: { filename: d.filename } }, editedMaterials: d.receivedDocs.map(doc => ({ type: doc.type, receivedQty: doc.qty })) });
    setAllocPanelOpen(true);
  }

  // Proxy receipts get an Ace-authored "Special Instructions" line (a package
  // summary) on disposition. Shared by Mark as Received and Received Short so the
  // two paths can't drift; non-Proxy → null (no instructions generated).
  const proxyInstructionNote = (d: ReceiptDraft) =>
    d.category === 'P'
      ? { author: 'Ace', timestamp: noteStamp(), text: buildProxyReceivedLines(d), kind: 'system' }
      : null;
  const postProxyInstructions = (note: { text: string } | null) => {
    if (note) addAgentMsg('Generating Special Instructions for this material match:\n\n```\n' + note.text + '\n```');
  };

  // Confirm a manual match → persist receipt edits + create the job link → Matched.
  async function handleConfirmMatch(receiptId: string) {
    const d = drafts[receiptId];
    addUserMsg('Confirm match');
    if (!d) { addAgentMsg('No receipt is linked to this thread — open it from the Intake Queue.'); return; }
    const jobs = draftToMatchJobs(d);
    showTyping();
    try {
      const patch = buildReceiptPatch(d);
      // Proxy match: capture the special-instructions line as an Ace-authored note.
      const siNote = proxyInstructionNote(d);
      if (siNote) patch.note_log = [...d.noteLog, siNote];
      // Regulatory match: MIC mail bucket creation is automatic — Material Type,
      // Supplier, Lit Code, Quantity, and Location are read straight off the
      // confirmed match instead of a clerk keying them into MIC by hand, so the
      // job enters the production pipeline already past "Create Mail Bucket".
      let micResult: Awaited<ReturnType<typeof createMicMailBucket>> | null = null;
      if (d.category === 'R' && jobs.length > 0) {
        micResult = await createMicMailBucket({
          materialType: d.materialType,
          supplier: d.distributor,
          litCode: d.litCode,
          quantity: d.quantity,
          location: d.location,
          mailingMethod: 'Pylon Logistic Standard',
          jobNumbers: jobs.map(j => j.job_number),
        });
        patch.regulatory_stage = 'ready_to_mail';
        const micNote = { author: 'Ace', timestamp: noteStamp(), text: formatMicBucketNote(micResult), kind: 'system' };
        patch.note_log = [...(patch.note_log ?? d.noteLog), micNote];
      }
      await updateMaterialReceipt(receiptId, patch);
      await confirmReceiptMatch(receiptId, jobs);
      pushReceiptToJobsDb({   // output (3): jobs-DB placeholder
        category: d.category, disposition: 'matched',
        received: d.receivedDocs.map(x => ({ type: x.type, receivedQty: x.qty })),
        jobs: jobs.map(j => ({ jobNumber: j.job_number, issuerName: '', materials: [] })),
      });
      const sess = sessions.find(s => s.id === activeId); if (sess) updateSession(sess.id, { receiptValidated: true });
      hideTyping();
      const jn = jobs.map(j => j.job_number);
      addAgentMsg(
        `Match confirmed — marked <strong>Matched</strong> and added to the Receiving Log` + (jn.length ? ` (Job #${jn.join(', ')}).` : '.'),
        [{ label: 'View Receiving Log', fn: () => navigate('/receiving-log') }],
      );
      if (micResult) {
        addAgentMsg(
          `Mail bucket automatically created in MIC — no manual entry needed:\n\n\`\`\`\n${formatMicBucketNote(micResult)}\n\`\`\``,
        );
      }
      postProxyInstructions(siNote);
      showToast?.(micResult ? `Matched · MIC bucket #${micResult.bucketId} created` : 'Matched · added to Receiving Log');
    } catch {
      hideTyping();
      addAgentMsg('Could not confirm the match — please try again.');
      showToast?.('Failed to confirm match');
    }
  }

  // Shared exception router: persist edits + PATCH status → navigate to the queue.
  async function routeException(receiptId: string, status: string, userLabel: string, route: string, reason?: string) {
    const d = drafts[receiptId];
    addUserMsg(userLabel);
    if (!d) { addAgentMsg('No receipt is linked to this thread — open it from the Intake Queue.'); return; }
    showTyping();
    try {
      const patch = buildReceiptPatch(d);
      // The "why" is a typed reason entry on the note log — attributed + stamped.
      if (reason) patch.note_log = [...d.noteLog, { author: firstName, timestamp: noteStamp(), text: reason, kind: 'reason' }];
      await updateMaterialReceipt(receiptId, { ...patch, material_status: status });
      const sess = sessions.find(s => s.id === activeId); if (sess) updateSession(sess.id, { receiptValidated: true });
      hideTyping();
      addAgentMsg(
        `Receipt routed to <strong>${status}</strong>${reason ? ` — <em>${reason}</em>` : ''}.`,
        [{ label: `View ${status}`, fn: () => navigate(route) }],
      );
      showToast?.(`Routed to ${status}`);
    } catch {
      hideTyping();
      addAgentMsg('Could not route the receipt — please try again.');
    }
  }

  // Received Short is one automatic step: persist edits, set the status, and push
  // straight into the Escalations queue (Cary) for the shortage followup — no
  // separate escalate action. The system note spells out which docs came up short.
  async function handleReceivedShort(receiptId: string) {
    const d = drafts[receiptId];
    addUserMsg('Received Short');
    if (!d) { addAgentMsg('No receipt is linked to this thread — open it from the Intake Queue.'); return; }
    showTyping();
    try {
      const patch = buildReceiptPatch(d);
      const detail = shortageSummary(d);
      const text = `Received short${detail ? ` — ${detail}` : ''}. Auto-escalated to ${teamLabel('cary')} for shortage followup.`;
      // Proxy receipts also get the Special Instructions line (same as Mark as Received).
      const siNote = proxyInstructionNote(d);
      await updateMaterialReceipt(receiptId, {
        ...patch,
        material_status: 'Received Short',
        escalated_to_team: 'cary',
        note_log: [
          ...d.noteLog,
          { author: firstName, timestamp: noteStamp(), text, kind: 'system' },
          ...(siNote ? [siNote] : []),
        ],
      });
      // Proxy hears about it in Ace, not by email: the notification shows in
      // their bell and Ace turns it into a shortage follow-up task.
      const jobNumbers = d.jobs.map(j => j.jobNumber).filter(Boolean);
      sendTeamMessage({
        from: 'wayne', to: 'cary', kind: 'received-short',
        title: `Received short: ${d.clientNames?.[0] || d.scan}`, body: text,
        jobId: jobNumbers[0], scan: d.scan, client: d.clientNames?.[0],
      });
      const sess = sessions.find(s => s.id === activeId); if (sess) updateSession(sess.id, { receiptValidated: true });
      hideTyping();
      addAgentMsg(
        `Logged to the Receiving Log and escalated to <strong>${teamLabel('cary')}</strong>${detail ? ` — <em>${detail}</em>` : ''}. It's now in the Escalations queue for the shortage followup.`,
        [{ label: 'View Escalations', fn: () => navigate('/escalations') }],
      );
      postProxyInstructions(siNote);
      showToast?.('Received short — escalated');
    } catch {
      hideTyping();
      addAgentMsg('Could not route the receipt — please try again.');
    }
  }
  // Open the Intake Queue's shortage follow-up for this receipt's job.
  function openShortageTask(sid: string, row: any) {
    const jobNums: string[] = row?.job_numbers || [];
    const task = buildProxyIntakeTasks(proxyCtxRef.current)
      .find(t => t.workflowType === 'SHORTAGE_FOLLOWUP' && jobNums.includes(t.jobId));
    if (task) openProxyTask(task);
    else addAgentMsgTo(sid, 'The shortage follow-up for this job is already done. The next chase, if one is due, is in Follow Ups.');
  }

  function handleNoJobFound(receiptId: string) {
    routeException(receiptId, 'No Job Found', 'Route to No Job Found', '/exceptions?type=no-job-found');
  }
  function handleHold(receiptId: string, reason?: string) {
    routeException(receiptId, 'On Hold', 'Put on hold', '/exceptions?type=on-hold', reason);
  }

  function runVerify(client, jobNum, sid) {
    const add = sid ? (content, actions, extras) => addAgentMsgTo(sid, content, actions, extras) : addAgentMsg;
    agentAfter(1000, () => {
      add('', [
        { label: 'Confirm match', fn: runConfirmMatch },
        { label: 'Reject', fn: () => addAgentMsg('No problem. Correct job number, or route to No Job Found?') },
      ], {
        card: { jobNum, client, type: 'Proxy', eventDate: 'Jan 28, 2026', material: 'Proxy stmt', confidence: 91 },
        content: '',
      });
    });
  }

  function runChase(client, jobNum, sid) {
    const add = sid ? (content, actions, extras) => addAgentMsgTo(sid, content, actions, extras) : addAgentMsg;
    const addUser = sid ? (text) => addUserMsgTo(sid, text) : addUserMsg;
    agentAfter(1200, () => {
      add(
        `Here's a draft chase email for Job #${jobNum} — ${client}. Review before I send it:<br><br><div style="background:#E9F6F6;border-radius:8px;padding:12px;font-size:14px;margin-top:4px;border-left:3px solid #14b8a6"><div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.06em;color:#73747A;margin-bottom:6px">Draft</div><div style="font-weight:600;margin-bottom:6px;color:#043243">Subject: Material reminder — ${client}, Job #${jobNum}</div><div style="color:#595A60;line-height:1.6">We are following up on the material shipment for the above job. We have not yet received the materials at our fulfillment center. Could you please confirm the shipping status and provide a tracking number if available?</div></div><br>Send this, edit it, or cancel?`,
        [
          { label: 'Send it', fn: () => { addUser('Send it'); setTimeout(() => { add(`Done — reminder sent to ${client}. Follow-up activity logged against Job #${jobNum}. I'll flag Proxy/ICT if there's no response by end of day.`); showToast?.('Email sent · Activity logged'); }, 1000); } },
          { label: 'Make it more urgent', fn: () => { addUser('Make it more urgent'); setTimeout(() => { add(`Updated — here's the revised draft:<br><br><div style="background:#FEF2F2;border-radius:8px;padding:12px;font-size:14px;border-left:3px solid #e7000b"><div style="font-weight:600;margin-bottom:6px;color:#043243">Subject: URGENT — Material not received · ${client}, Job #${jobNum}</div><div style="color:#595A60;line-height:1.6">This is a time-sensitive follow-up. Materials for Job #${jobNum} have not arrived and the event date is approaching. Please confirm shipping status and tracking immediately.</div></div><br>Send this?`, [{ label: 'Send it', fn: () => { addUser('Send it'); setTimeout(() => { add(`Urgent reminder sent. Activity logged against Job #${jobNum}.`); showToast?.('Urgent email sent · Activity logged'); }, 800); } }, { label: 'Cancel', fn: () => add('Cancelled.') }]); }, 900); } },
          { label: 'Cancel', fn: () => addAgentMsg('No problem — cancelled. Scan stays in the queue.') },
        ]
      );
    });
  }

  function runFlag(jobNum) {
    agentAfter(900, () => {
      addAgentMsg(`Job #${jobNum} flagged as <strong>urgent</strong>. Who should I notify — Proxy/ICT, Receiving, or both?`, [
        { label: 'Both', fn: () => { addUserMsg('Both'); setTimeout(() => { addAgentMsg('Done. Both teams notified.'); showToast?.('Urgent flag · Both teams notified'); }, 800); } },
        { label: 'Proxy/ICT only', fn: () => { addUserMsg('Proxy/ICT only'); setTimeout(() => addAgentMsg('Done. Proxy/ICT notified.'), 800); } },
      ]);
    });
  }

  function runDiscard(scanName) {
    agentAfter(800, () => {
      addAgentMsg(`Confirm — I'll remove <strong>${scanName}</strong> from the queue entirely. This can't be undone.`, [
        { label: 'Yes, discard', fn: () => { addUserMsg('Yes, discard'); setTimeout(() => { addAgentMsg(`${scanName} has been removed.`); showToast?.(`${scanName} discarded`); }, 800); } },
        { label: 'Cancel', fn: () => addAgentMsg('Cancelled. Scan remains in the queue.') },
      ]);
    });
  }

  function runAddRegulatory() {
    agentAfter(1000, () => {
      addAgentMsg('Sure — I\'ll add it to the regulatory log. Give me the <strong>client name</strong>, <strong>job number</strong>, and <strong>filing type</strong>. For example: Apple Inc, job #3010900, annual report.<br><br><span style="font-size:12px;color:#73747A">Note: regulatory jobs don\'t have an event date — I won\'t ask for one.</span>');
    });
  }

  const proxyCtx = {
    jobs: proxyJobs,
    appendFollowUp: proxyAppendFollowUp,
    addAgentMsg,
    navigate,
    showToast,
    openRdqOrderTask: (job) => openProxyTask(buildRdqOrderTask(job, 'job_signal')),
  };

  // Ace's Proxy landing-state cards — real numbers off the workflow registry
  // and the same primitives the Proxy pages themselves use, not a bespoke
  // recomputation. Cheap enough (≈50 jobs) to compute on every render rather
  // than gate behind activeSection, since hooks can't be conditional anyway.
  const proxyIntakeSummary = buildProxyIntakeSummary(proxyWorkflowCtx);
  const proxyResolvedJobs = proxyJobs.map(j => resolveProxyJob(j, proxyWorkflowCtx.receipts));
  const proxyCutoff10 = proxyResolvedJobs.filter(j => j.effectiveStatus === 'ACTIVE' && j.daysToVoteCutoff <= 10).length;
  const proxyCutoff5 = proxyResolvedJobs.filter(j => j.effectiveStatus === 'ACTIVE' && j.daysToVoteCutoff <= 5).length;
  const proxyOpenContests = proxyJobs.filter(j => j.contest && j.contest.formStatus !== 'SENT_TO_DISCOVERY').length;
  const proxyShortagesAwaiting = proxyJobs.filter(j => j.shortageLines?.length && !hasOnlySkippedMaterial(j) && !shortageResolved(j)).length;

  const proxyStateCards: FloorCard[] = [
    {
      key: 'proxy-tasks', count: proxyIntakeSummary.tasksCreated, severity: 'neutral',
      fact: `${proxyIntakeSummary.tasksCreated} tasks in the Intake Queue`,
      recommendation: `From ${proxyIntakeSummary.emailsRead} emails Ace has read`,
      action: { label: 'View', kind: 'view', route: '/proxy' },
    },
    {
      key: 'proxy-review', count: proxyIntakeSummary.needReview, severity: proxyIntakeSummary.needReview > 0 ? 'amber' : 'neutral',
      fact: `${proxyIntakeSummary.needReview} item${proxyIntakeSummary.needReview === 1 ? '' : 's'} need review`,
      recommendation: 'Low confidence or missing a solicitor contact',
      action: { label: 'View', kind: 'view', route: '/proxy/exceptions' },
    },
    {
      key: 'proxy-cutoff', count: proxyCutoff10, severity: proxyCutoff5 > 0 ? 'danger' : (proxyCutoff10 > 0 ? 'amber' : 'neutral'),
      fact: `${proxyCutoff10} hitting cutoff within 10 days (${proxyCutoff5} within 5 days)`,
      recommendation: 'Red-band jobs — POD, email-only/cancel, or abort depending on exact timing',
      action: { label: 'View', kind: 'view', route: '/proxy/reports/outstanding-red' },
    },
    {
      key: 'proxy-contests', count: proxyOpenContests, severity: 'purple',
      fact: `${proxyOpenContests} open proxy contest${proxyOpenContests === 1 ? '' : 's'}`,
      recommendation: 'Working through the signature state machine',
      action: { label: 'View', kind: 'view', route: '/proxy' },
    },
    {
      key: 'proxy-shortages', count: proxyShortagesAwaiting, severity: proxyShortagesAwaiting > 0 ? 'danger' : 'neutral',
      fact: `${proxyShortagesAwaiting} shortage${proxyShortagesAwaiting === 1 ? '' : 's'} awaiting response`,
      recommendation: 'Material received below what was ordered',
      action: { label: 'View', kind: 'view', route: '/proxy' },
    },
  ];
  const proxyStateClear = proxyStateCards.every(c => c.count === 0);

  // Same one-tap deep-link pattern as handleFloorAction, minus the
  // pageVisible() gate — Proxy has zero RBAC gating by design.
  const handleProxyStateAction = useCallback((card: FloorCard) => {
    trackEvent('floor_action_click', { key: card.key });
    const { kind, route } = card.action;
    if (kind === 'view' && route) { navigate(route); return; }
    sendText(`Tell me more about ${card.fact.toLowerCase()}.`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  async function handleUserMessage(text) {
    addUserMsg(text);
    const proxyIntent = matchProxyIntent(text);
    if (proxyIntent) {
      runProxyIntent(proxyIntent, proxyCtx);
      return;
    }
    try {
      showTyping();
      const hist = (msgs || []).slice(-6).map(m => ({ role: m.role, content: m.content }));
      const resp = await chatWithAgent(text, 'ace', hist, []);
      hideTyping();
      addAgentMsg(resp.message);
    } catch (err) {
      hideTyping();
      console.error('Ace chat error:', err);
      addAgentMsg('Something went wrong. Please try again.');
    }
  }

  function sendText(text) {
    if (!text.trim()) return;
    handleUserMessage(text);
  }

  function handleSend() {
    if (!input.trim() || isLoading) return;
    const text = input.trim();
    setInput('');
    handleUserMessage(text);
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }

  useEffect(() => {
    function handleGlobalKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault();
        newSession({ kind: 'manual' });
      }
    }
    window.addEventListener('keydown', handleGlobalKey);
    return () => window.removeEventListener('keydown', handleGlobalKey);
  }, [newSession]);

  function handleQaClick(action) {
    if (action.fn) action.fn();
    else if (action.text) sendText(action.text);
  }

  const firstName = user?.name?.split(' ')[0] || 'User';

  // Shared chat composer — scaffold .chat-input-shell + round .send-fab
  const composer = (
    <div className="chat-input-shell px-3.5 pt-3 pb-2">
      <textarea
        data-tour="ace-input"
        className="w-full bg-transparent resize-none outline-none text-sm text-ink leading-relaxed min-h-[24px] max-h-[120px] font-[inherit]"
        placeholder="Ask anything — search a job, chase material, flag urgent…"
        rows={1}
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={handleKey}
      />
      <div className="flex items-center justify-between mt-1.5">
        <button type="button" className="composer-tool" onClick={triggerUpload} title="Attach a scan">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
          Attach
        </button>
        <button className="send-fab" onClick={handleSend} disabled={!input.trim() || isLoading} title="Send">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 19V5M5 12l7-7 7 7" />
          </svg>
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-full bg-white">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,image/*"
        multiple
        className="hidden"
        onChange={handleFilesSelected}
      />

      {/* ── Left column ───────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {msgs.length === 0 ? (

          /* ── Landing cockpit: floor state → capture CTA → ambient chat ── */
          <div className="flex-1 overflow-y-auto flex flex-col items-center px-8 py-10">
            <div className="w-full max-w-[560px]">
              <h1 className="text-base text-muted tracking-wide mb-6">Welcome back, {firstName}</h1>

              {/* Demoted hero avatar + typewriter — Floor State is the hero now */}
              <div className="flex items-center gap-3 mb-6">
                <AgentAvatar size={36} hero />
                <span className="font-lora text-base text-copy">
                  {displayText}
                  <span className="inline-block w-0.5 h-4 bg-teal-500 ml-0.5 align-middle animate-pulse" />
                </span>
              </div>

              {/* Region 1 — state cards (proactive AI cards), swapped per active section */}
              <div className="mb-6">
                <h2 className="text-xs font-semibold text-muted uppercase tracking-widest mb-3">
                  {activeSection === 'proxy' ? 'Proxy state' : 'Floor state'}
                </h2>
                {activeSection === 'proxy' ? (
                  proxyStateClear ? (
                    <div className="px-4 py-6 rounded-xl border border-trim bg-wash text-center">
                      <div className="text-sm font-medium text-[#0F6E56] mb-0.5">Queue is clear</div>
                      <div className="text-xs text-muted">Nothing needs attention right now.</div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {proxyStateCards.map(card => (
                        <FloorStateCard key={card.key} card={card} onAction={handleProxyStateAction} />
                      ))}
                    </div>
                  )
                ) : (
                  floorLoading ? (
                    <div className="text-sm text-muted py-2">Checking the floor…</div>
                  ) : !floorState || floorState.is_clear || floorState.cards.length === 0 ? (
                    <div className="px-4 py-6 rounded-xl border border-trim bg-wash text-center">
                      <div className="text-sm font-medium text-[#0F6E56] mb-0.5">Floor is clear</div>
                      <div className="text-xs text-muted">Nothing needs attention right now.</div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {floorState.cards.map(card => (
                        <FloorStateCard key={card.key} card={card} onAction={handleFloorAction} />
                      ))}
                    </div>
                  )
                )}
              </div>

              {/* Region 2 — primary Scan CTA + secondary Upload (Fulfillment only) */}
              {activeSection !== 'proxy' && (
                <div className="flex items-center gap-3 mb-6">
                  <button
                    onClick={openCameraCapture}
                    className="flex-1 px-4 py-3 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-cyan-500 hover:shadow-lg transition-shadow"
                  >
                    Scan material
                  </button>
                  <button
                    onClick={triggerUpload}
                    className="px-4 py-3 rounded-lg text-sm font-medium text-copy border border-trim hover:bg-wash transition-colors"
                  >
                    Upload Scan
                  </button>
                </div>
              )}

              {/* Region 3 — Ambient chat bar */}
              {composer}
              <div className="flex flex-wrap gap-2 justify-center mt-4">
                {(activeSection === 'proxy' ? [
                  { label: 'Collections Summary', fn: () => runProxyIntent({ handler: 'collectionsSummary', params: {} }, proxyCtx) },
                  { label: 'Draft Red Follow-Ups', fn: () => runProxyIntent({ handler: 'draftRedFollowUps', params: {} }, proxyCtx) },
                  { label: 'RDQ Needed', fn: () => runProxyIntent({ handler: 'rdqNeeded', params: {} }, proxyCtx) },
                ] : [
                  { label: 'Floor Summary', fn: () => sendText('Give me a floor summary.') },
                ]).map(chip => (
                  <button
                    key={chip.label}
                    className="px-4 py-2 rounded-full border border-trim text-sm text-copy bg-white hover:bg-tint hover:border-teal-400 hover:text-ink transition-all"
                    onClick={chip.fn}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

        ) : (

          /* ── Chat state: scrollable messages + bottom input ── */
          <>
            <div className="flex-1 overflow-y-auto">
              <div className="max-w-[720px] mx-auto w-full px-4 py-5 flex flex-col">
                {msgs.length > 0 && (
                  <div className="thread-section">
                    <span className="thread-section-rule" />
                    <span className="thread-section-label">
                      <span className="thread-section-title">{activeSess?.title || 'New conversation'}</span>
                      {msgs[0]?.time ? <span className="thread-section-time">{String(msgs[0].time)}</span> : null}
                    </span>
                    <span className="thread-section-rule" />
                  </div>
                )}
                {(() => {
                  const units = buildUnits(msgs);
                  const agentish = (u) => !!u && (u.type === 'trace' || u.msg.role === 'agent');
                  // Agent is "responding" while the latest unit is a live trace
                  // group (a workflow step still streaming) or the typing dots show.
                  const streaming = isLoading || units[units.length - 1]?.type === 'trace';
                  return units.map((u, i) => {
                    const showAvatar = agentish(u) && !agentish(units[i - 1]);
                    const isLast = i === units.length - 1;
                    if (u.type === 'trace') {
                      return <TraceGroup key={u.key} items={u.items} isLatest={isLast} showAvatar={showAvatar} thinking={showAvatar && streaming} />;
                    }
                    const msg = u.msg;
                    const isUser = msg.role === 'user';
                    const firstInGroup = isUser ? true : showAvatar;
                    const lastInGroup = isUser ? true : !agentish(units[i + 1]);
                    return (
                      <ChatMessage
                        key={msg.id}
                        msg={msg}
                        draft={msg.receiptId ? drafts[msg.receiptId] : undefined}
                        firstInGroup={firstInGroup}
                        lastInGroup={lastInGroup}
                        avatarThinking={!isUser && showAvatar && streaming}
                        onQaClick={handleQaClick}
                        onConfirmMatch={handleConfirmMatch}
                        onNoJobFound={handleNoJobFound}
                        onReceivedShort={handleReceivedShort}
                        onHold={handleHold}
                        onReviewEdit={handleReviewEdit}
                        onViewPdf={(t) => { setPdfTarget(t); setPdfPanelOpen(true); }}
                        onOpenAllocation={handleOpenAllocation}
                        onReviewProxyTaskSnapshot={handleReviewProxyTaskSnapshot}
                      />
                    );
                  });
                })()}
                {isLoading && (
                  <div className="self-start mt-4"><TypingIndicator /></div>
                )}
                <div ref={chatEndRef} />
              </div>
            </div>
            <div className="px-6 py-5 border-t border-trim bg-white flex-shrink-0">
              <div className="max-w-[680px] mx-auto">
                {composer}
              </div>
            </div>
          </>

        )}
      </div>

      {/* ── Right panel ───────────────────────────────── */}
      {((activeSess?.kind === 'email' && activeSess.caEmail)
        || (activeSess?.kind === 'advisor-email' && activeSess.advisorEmail)
        || (proxyPanelVisible && activeSess?.proxyTask)) && (
        <div className="relative flex-shrink-0 border-l border-trim bg-white overflow-y-auto flex flex-col" style={{ width: panelWidth }}>
          {/* Drag handle */}
          <div
            className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-teal-400/40 transition-colors z-10"
            onMouseDown={(e) => {
              isResizing.current = true;
              resizeStartX.current = e.clientX;
              resizeStartWidth.current = panelWidth;
              document.body.style.cursor = 'col-resize';
              document.body.style.userSelect = 'none';
            }}
          />

          {activeSess?.kind === 'email' && activeSess.caEmail ? (
            <CAWorkflowPanel email={activeSess.caEmail} sessionId={activeId!} />
          ) : activeSess?.kind === 'advisor-email' && activeSess.advisorEmail ? (
            <AdvisorRegistrationPanel email={activeSess.advisorEmail} sessionId={activeId!} />
          ) : proxyPanelVisible && activeSess?.proxyTask ? (
            <ProxyTaskPanel key={`${activeId}:${activeSess.proxyTask.id}:${activeSess.proxyTaskApproved ? 'approved' : 'open'}`} task={activeSess.proxyTask} sessionId={activeId!} />
          ) : null}
        </div>
      )}

      {/* ── Modals ────────────────────────────────────── */}
      {camOpen && (
        <CameraModal
          onClose={() => setCamOpen(false)}
          onFindJob={async ({ jobType, materialCaptures }) => {
            setCamOpen(false);
            const category = ({ proxy: 'P', ca: 'CA', regulatory: 'R', issuer: 'I' } as Record<string,string>)[jobType] || 'P';
            const scanNumber = generateScanNumber(category);
            try {
              const created = await createMaterialReceipt({
                scan_number: scanNumber,
                intake_source: 'ace_camera_demo',
                job_category: category,
                // Use operator local date so the receipt lands in "Today" window.
                intake_date: todayIso(),
                intake_by: user?.name?.split(' ')[0] || 'Operator',
                material_status: 'New',
              });
              showToast?.(`Receipt ${scanNumber} created`);
              // Land back in Ace's own thread instead of navigating away — this is
              // what lets the "Ask Ace why" hook (in presentExceptionCard) surface
              // later if/when matching flags this receipt as an exception.
              openReceiptThread({ receiptId: created.id, scan: scanNumber, row: created });
            } catch {
              showToast?.('Failed to create receipt — please try again');
            }
          }}
          onSaveAndNext={async ({ jobType, materialCaptures }) => {
            const category = ({ proxy: 'P', ca: 'CA', regulatory: 'R', issuer: 'I' } as Record<string,string>)[jobType] || 'P';
            const scanNumber = generateScanNumber(category);
            try {
              await createMaterialReceipt({
                scan_number: scanNumber,
                intake_source: 'ace_camera_demo',
                job_category: category,
                intake_date: todayIso(),
                intake_by: user?.name?.split(' ')[0] || 'Operator',
                material_status: 'New',
              });
              showToast?.(`Receipt ${scanNumber} created — ready for next capture`);
            } catch {
              showToast?.('Failed to create receipt — please try again');
            }
          }}
        />
      )}
      <ReceiptDrawer
        open={drawerOpen}
        draft={drawerReceiptId ? drafts[drawerReceiptId] : null}
        author={firstName}
        onChange={patch => drawerReceiptId && updateDraft(drawerReceiptId, patch)}
        onClose={() => setDrawerOpen(false)}
        onSave={handleDrawerSave}
      />
      <PdfViewerPanel
        open={pdfPanelOpen}
        target={pdfTarget}
        onClose={() => setPdfPanelOpen(false)}
        rightOffset={drawerOpen ? 576 : 0}
      />
      {allocTarget && (
        <MaterialAllocationPanel
          open={allocPanelOpen}
          onClose={() => setAllocPanelOpen(false)}
          onSave={() => {}}
          onReviewed={allocTarget?.onReviewed}
          result={allocTarget.result}
          editedMaterials={allocTarget.editedMaterials}
        />
      )}
      {escalateTarget && (
        <EscalateModal
          mode={escalateTarget.mode}
          onClose={() => setEscalateTarget(null)}
          onSubmit={(note, role) => submitEscalation(escalateTarget.receiptId, escalateTarget.mode, note, role)}
        />
      )}
      {mergeTarget && (
        <MergePickerModal
          targetId={mergeTarget}
          onClose={() => setMergeTarget(null)}
          onPick={(sourceId) => submitMerge(mergeTarget, sourceId)}
        />
      )}
      {reviewingSnapshot && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(4,50,67,.5)' }} onClick={e => { if (e.target === e.currentTarget) setReviewingSnapshot(null); }}>
          <div className="relative bg-white rounded-2xl shadow-modal w-[640px] max-h-[85vh] overflow-y-auto">
            <button onClick={() => setReviewingSnapshot(null)} className="absolute right-4 top-4 z-10 text-muted hover:text-ink text-lg leading-none">×</button>
            <ProxyTaskPanel
              key={reviewingSnapshot.task.id}
              task={reviewingSnapshot.task}
              sessionId={activeId!}
              readOnly
              approvedOverride={reviewingSnapshot.approved}
            />
          </div>
        </div>
      )}
    </div>
  );
}

